import { useEffect, useMemo, useState } from "react";
import {
  LogIn,
  LogOut as LogOutIcon,
  Clock3,
  MapPin,
  ShieldCheck,
  Camera,
  Navigation,
  UserCircle2,
  PartyPopper,
  CalendarOff,
  Moon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CameraCapture } from "@/components/CameraCapture";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthImage } from "@/components/AuthImage";
import { formatInTZ, tzShort } from "@/lib/tz";

function useLiveClock(tz) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return {
    time: tz
      ? formatInTZ(now, tz, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
      : "--:--:--",
    date: tz
      ? formatInTZ(now, tz, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
      : "",
  };
}

const GEO_ERROR_MSG =
  'Gagal mengambil lokasi. Mohon pastikan GPS aktif dan izin lokasi diatur ke "Allow" pada pengaturan browser/HP Anda.';

// Opsi geolocation dioptimasi untuk perangkat iOS:
// timeout lebih panjang (15 dtk) & maximumAge 30 dtk agar sinyal GPS
// sempat terkunci dan tidak langsung dianggap 'denied' saat jaringan lambat.
const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 };

async function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error(GEO_ERROR_MSG));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => reject(new Error(GEO_ERROR_MSG)),
      GEO_OPTIONS
    );
  });
}

// Haversine — jarak (meter) antara dua koordinat.
function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

export default function Dashboard() {
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'checkin' | 'checkout' | null
  const [cameraOpen, setCameraOpen] = useState(false);
  const [fotoProfilPath, setFotoProfilPath] = useState(null);
  const [distanceMeter, setDistanceMeter] = useState(null);
  const [geoError, setGeoError] = useState(null);

  // Foto profil diambil dari Biodata (self-service). Silent — bila belum
  // diisi tampilkan placeholder.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/biodata/mine");
        if (alive) setFotoProfilPath(data.field_upload_foto_profil || null);
      } catch {
        /* silent */
      }
    })();
    return () => { alive = false; };
  }, []);

  async function loadToday() {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/today");
      setSnapshot(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    loadToday();
  }, []);

  const tz = snapshot?.timezone;
  const { time, date } = useLiveClock(tz);
  const record = snapshot?.record;
  const lokasi = snapshot?.lokasi;
  const jadwal = snapshot?.jadwal;
  const isHoliday = !!snapshot?.is_holiday;
  const isOff = !!snapshot?.is_off;
  const isLiburOff = isHoliday || isOff;
  const holidayNama = snapshot?.holiday_nama;
  const otToday = snapshot?.overtime_today;

  // Live tracking jarak user ↔ kantor. Menggunakan watchPosition agar teks
  // keterangan jarak ter-update otomatis saat koordinat berhasil diambil.
  const officeLat = lokasi?.latitude;
  const officeLng = lokasi?.longitude;
  useEffect(() => {
    if (officeLat == null || officeLng == null) return;
    if (!navigator.geolocation) {
      setGeoError(GEO_ERROR_MSG);
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setGeoError(null);
        setDistanceMeter(
          haversineMeters(pos.coords.latitude, pos.coords.longitude, officeLat, officeLng)
        );
      },
      () => {
        setGeoError(GEO_ERROR_MSG);
        setDistanceMeter(null);
      },
      GEO_OPTIONS
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [officeLat, officeLng]);

  const hasCheckIn = !!record?.check_in_at;
  const hasCheckOut = !!record?.check_out_at;

  function startCheckIn() {
    setMode("checkin");
    setCameraOpen(true);
  }
  function startCheckOut() {
    setMode("checkout");
    setCameraOpen(true);
  }

  async function submitSelfie(dataUrl) {
    setCameraOpen(false);
    setBusy(true);
    try {
      const pos = await getCurrentPosition();
      const body = {
        selfie_base64: dataUrl,
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
      };
      const url = mode === "checkin" ? "/attendance/checkin" : "/attendance/checkout";
      const { data } = await api.post(url, body);
      toast.success(mode === "checkin" ? "Check-In berhasil" : "Check-Out berhasil");
      await loadToday();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
      setMode(null);
    }
  }

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="page-dashboard">
      {/* Header */}
      <div className="staggered-fade">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Foto profil dari Biodata */}
            <div className="shrink-0" data-testid="dashboard-foto-profil">
              {fotoProfilPath ? (
                <AuthImage
                  path={fotoProfilPath}
                  kind="biodata"
                  alt="Foto Profil"
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 border-teal-100 shadow-sm"
                />
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 border-teal-100 bg-teal-50 flex items-center justify-center text-teal-700">
                  <UserCircle2 size={44} />
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
                Hari Ini
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
                Halo, {user?.name?.split(" ")[0] || "Pegawai"}.
              </h1>
              <p className="text-slate-500 text-sm mt-1">{date || "Memuat..."}</p>
            </div>
          </div>
        </div>

        {/* Banner Hari Libur / OFF */}
        {!loading && isLiburOff ? (
          <div
            className={`mt-6 rounded-xl border p-4 sm:p-5 flex items-start gap-3 ${
              isHoliday ? "border-rose-200 bg-rose-50" : "border-indigo-200 bg-indigo-50"
            }`}
            data-testid="libur-off-banner"
          >
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                isHoliday ? "bg-rose-100 text-rose-700" : "bg-indigo-100 text-indigo-700"
              }`}
            >
              {isHoliday ? <PartyPopper size={20} /> : <CalendarOff size={20} />}
            </div>
            <div className="min-w-0">
              <div
                className={`font-display text-base sm:text-lg font-extrabold leading-tight ${
                  isHoliday ? "text-rose-800" : "text-indigo-800"
                }`}
                data-testid="libur-off-label"
              >
                {isHoliday
                  ? `Hari Libur Nasional: ${holidayNama || "Libur"}`
                  : "Jadwal: OFF"}
              </div>
              <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">
                {otToday ? (
                  <>
                    Anda memiliki <b>Perintah Lembur</b> hari ini{" "}
                    <span className="font-mono-clock font-semibold text-slate-800">
                      {otToday.rencana_mulai}–{otToday.rencana_selesai}
                    </span>{" "}
                    ({otToday.rencana_jam} jam). Silakan <b>Check-In</b> saat mulai &amp;{" "}
                    <b>Check-Out</b> saat selesai. Durasi lembur dihitung <b>maksimal sesuai rencana</b>.
                  </>
                ) : (
                  <>Hari ini di luar jadwal kerja. Fitur absensi lembur tetap dapat digunakan bila ada Perintah Lembur.</>
                )}
              </p>
            </div>
          </div>
        ) : null}

        {/* Hero card */}
        <div
          className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden hover-lift"
          data-testid="hero-card"
        >
          <div className="grid md:grid-cols-5">
            {/* Clock side */}
            <div
              className={`md:col-span-2 relative p-6 sm:p-8 text-white ${
                isHoliday
                  ? "bg-gradient-to-br from-rose-900 to-rose-700"
                  : isOff
                    ? "bg-gradient-to-br from-indigo-900 to-indigo-700"
                    : "bg-gradient-to-br from-teal-900 to-teal-800"
              }`}
            >
              <div
                className={`text-[11px] uppercase tracking-[0.28em] font-bold ${
                  isHoliday ? "text-rose-200" : isOff ? "text-indigo-200" : "text-teal-200"
                }`}
              >
                {isLiburOff ? (isHoliday ? "Hari Libur" : "Hari OFF") : "Jam Lokasi"}
              </div>
              <div className="mt-2 font-mono-clock text-5xl sm:text-6xl font-bold" data-testid="live-clock">
                {loading ? "--:--:--" : time}
              </div>
              <div className={`mt-2 text-sm ${isHoliday ? "text-rose-100" : isOff ? "text-indigo-100" : "text-teal-100"}`}>
                {tz ? `Zona waktu: ${tz} (${tzShort(tz)})` : "—"}
              </div>
              {isLiburOff ? (
                <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-white/15 border border-white/25 rounded-full px-3 py-1" data-testid="lembur-mode-badge">
                  <Moon size={12} /> Mode Absensi Lembur
                </div>
              ) : null}

              <div
                className={`absolute -right-10 -bottom-10 w-40 h-40 rounded-full blur-3xl ${
                  isHoliday ? "bg-rose-500/20" : isOff ? "bg-indigo-500/20" : "bg-teal-500/20"
                }`}
              />
            </div>

            {/* Action side */}
            <div className="md:col-span-3 p-6 sm:p-8">
              {loading ? (
                <div className="text-slate-500 text-sm">Memuat status hari ini…</div>
              ) : (
                <>
                  <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-slate-500">
                    {isLiburOff ? "Status Absensi Lembur" : "Status Absensi"}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="dot-pulse" />
                      <span className="text-slate-700 text-sm">
                        {hasCheckOut
                          ? "Selesai"
                          : hasCheckIn
                            ? "Sedang bekerja"
                            : "Belum absen"}
                      </span>
                    </div>
                    {user?.wfa_permanen ? (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-purple-700 bg-purple-50 border border-purple-200 rounded px-2 py-0.5">
                        WFA Permanen
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-200 p-4">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                        Check-In
                      </div>
                      <div className="mt-1 font-mono-clock text-xl font-bold text-slate-900" data-testid="checkin-time">
                        {record?.check_in_lokal || "—"}
                      </div>
                      <div className="mt-1"><StatusBadge status={record?.check_in_status} testId="checkin-status" /></div>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-4">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                        Check-Out
                      </div>
                      <div className="mt-1 font-mono-clock text-xl font-bold text-slate-900" data-testid="checkout-time">
                        {record?.check_out_lokal || "—"}
                      </div>
                      <div className="mt-1"><StatusBadge status={record?.check_out_status} testId="checkout-status" /></div>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    {!hasCheckIn ? (
                      <Button
                        onClick={startCheckIn}
                        disabled={busy}
                        className="flex-1 h-12 bg-teal-700 hover:bg-teal-800 text-white font-bold text-base gap-2"
                        data-testid="checkin-btn"
                      >
                        <LogIn size={18} /> Check-In
                      </Button>
                    ) : !hasCheckOut ? (
                      <Button
                        onClick={startCheckOut}
                        disabled={busy}
                        className="flex-1 h-12 bg-rose-600 hover:bg-rose-700 text-white font-bold text-base gap-2"
                        data-testid="checkout-btn"
                      >
                        <LogOutIcon size={18} /> Check-Out
                      </Button>
                    ) : (
                      <div className="flex-1 h-12 rounded-md border-2 border-emerald-200 bg-emerald-50 text-emerald-800 font-bold text-sm flex items-center justify-center gap-2" data-testid="done-state">
                        <ShieldCheck size={16} /> Absensi hari ini sudah lengkap
                      </div>
                    )}
                  </div>

                  <p className="mt-3 text-xs text-slate-500 flex items-start gap-1.5">
                    <Camera size={12} className="mt-0.5" />
                    Anda akan diminta memberikan izin kamera & lokasi (GPS) sebelum absen.
                  </p>

                  {geoError ? (
                    <div
                      className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-1.5"
                      data-testid="geo-error"
                    >
                      <MapPin size={13} className="mt-0.5 shrink-0" />
                      <span>{geoError}</span>
                    </div>
                  ) : distanceMeter != null ? (
                    <div
                      className="mt-3 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-800 flex items-center gap-1.5"
                      data-testid="geo-distance"
                    >
                      <Navigation size={13} className="shrink-0" />
                      Jarak Anda dari kantor: {distanceMeter} meter
                    </div>
                  ) : (
                    <div
                      className="mt-3 text-xs text-slate-400 flex items-center gap-1.5"
                      data-testid="geo-loading"
                    >
                      <Navigation size={13} className="shrink-0 animate-pulse" />
                      Mendeteksi lokasi Anda…
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Info tiles */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <InfoTile
            icon={<MapPin size={16} />}
            title="Lokasi Kerja"
            value={lokasi?.nama || "—"}
            hint={lokasi?.alamat}
            testId="tile-lokasi"
          />
          <InfoTile
            icon={<Clock3 size={16} />}
            title="Jadwal Kerja"
            value={jadwal?.nama || "—"}
            hint={jadwal ? `${jadwal.jam_masuk} – ${jadwal.jam_pulang}` : ""}
            testId="tile-jadwal"
          />
          <InfoTile
            icon={<Navigation size={16} />}
            title="Radius Absensi"
            value={lokasi ? `${lokasi.radius_meter} meter` : "—"}
            hint={user?.wfa_permanen ? "Anda WFA Permanen — radius dilewati" : "Wajib berada dalam radius saat absen"}
            testId="tile-radius"
          />
        </div>
      </div>

      {/* Camera modal */}
      <CameraCapture
        open={cameraOpen}
        onClose={() => {
          setCameraOpen(false);
          setMode(null);
        }}
        onCapture={submitSelfie}
        testIdPrefix={mode === "checkin" ? "checkin-camera" : "checkout-camera"}
      />

      {busy ? (
        <div className="fixed inset-0 bg-slate-900/40 z-40 flex items-center justify-center" data-testid="submit-busy">
          <div className="bg-white rounded-lg px-6 py-5 flex items-center gap-3 border border-slate-200">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-teal-700 border-t-transparent" />
            <span className="text-sm font-semibold text-slate-800">Mengirim absensi…</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function InfoTile({ icon, title, value, hint, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 hover-lift" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-teal-700">
        {icon}
        {title}
      </div>
      <div className="mt-2 text-slate-900 font-semibold text-base leading-tight">{value}</div>
      {hint ? <div className="mt-1 text-xs text-slate-500 leading-relaxed">{hint}</div> : null}
    </div>
  );
}
